import React, { useCallback, useState, useEffect } from 'react'
import { MyModal } from '~/components/ui/MyModal'
import { MyForm } from '~/components/ui/MyForm'
import { Col, Input, message, Row } from 'antd'
import { graphQLErrorMessage, isIdCard } from '~/utils'
import styled from 'styled-components'
import { MyBtn } from '~/components/ui/MyBtn'
import { REAL_NAME_AUTH, REAL_NAME_AUTH_COMPLETION } from '~/graphqls/gqls'
import { useMutation } from '@apollo/client'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react'
import {
  CopyrightAuthStatus,
  OssBucket,
  realNameAuth,
  realNameAuthVariables,
  realNmaeAuthCompletion,
  realNmaeAuthCompletionVariables,
  getAuthUserInfo_copyrightAuthInfoPlaintext,
} from '~/graphqls/types'
import { useAuth } from '~/hooks/useAuth'
import { PictureCardUpload } from '~/components/business/PictureCardUpload'
import _ from 'lodash'

const StyledLabel = styled.div`
  font-size: 12px;
  line-height: 20px;
  margin-top: -24px;
  margin-bottom: 24px;
  color: ${props => props.theme.dark4};
`

export const RealNameModal: React.FC<{
  visible: boolean
  setVisible: (v: boolean) => void
  authInfo?: getAuthUserInfo_copyrightAuthInfoPlaintext | null
}> = observer(({ visible, setVisible, authInfo }) => {
  const { store } = useStores()
  const [form] = MyForm.useForm()
  const [loading, setLoading] = useState(false)
  const { needCompletionRealName, authStatus } = useAuth()
  const [authAction] = useMutation<realNameAuth, realNameAuthVariables>(
    REAL_NAME_AUTH
  )
  const [completionAction] = useMutation<
    realNmaeAuthCompletion,
    realNmaeAuthCompletionVariables
  >(REAL_NAME_AUTH_COMPLETION)
  useEffect(() => {
    let initialValues = {}
    if (needCompletionRealName) {
      initialValues = {
        idCard: store.userinfo?.authInfo?.idCard,
        realName: store.userinfo?.authInfo?.realName,
      }
    } else if (authInfo && Object.keys(authInfo).length > 0) {
      // 身份证照片放在的私有的bucket中，需要token去访问，这里直接不填充，没有影响
      initialValues = {
        idCard: authInfo.idCard,
        realName: authInfo.realName,
      }
    }
    form.setFieldsValue(initialValues)
  }, [authInfo, visible])
  /** 是否可编辑姓名与身份证 */
  const isDisabled = needCompletionRealName
    ? needCompletionRealName
    : ![CopyrightAuthStatus.AUTH_FAIL, CopyrightAuthStatus.NOT_AUTH].includes(
        authStatus as CopyrightAuthStatus
      )
  const onConfirm = useCallback(async () => {
    const formData = await form.validateFields()

    setLoading(true)

    let confirmPromise: Promise<any>

    if (needCompletionRealName) {
      confirmPromise = completionAction({
        variables: {
          param: _.omit(formData, ['realName', 'idCard']),
        },
        fetchPolicy: 'no-cache',
      })
    } else {
      confirmPromise = authAction({
        variables: {
          param: formData,
        },
        fetchPolicy: 'no-cache',
      })
    }

    confirmPromise
      .then(data => {
        message.destroy()
        setVisible(false)
        if (data.data?.status) {
          message.success('提交成功，正在认证中～')
          store.userinfo?.setAuthAtatus(CopyrightAuthStatus.AUTH_ING)
        } else {
          message.error('实名认证失败')
          store.userinfo?.setAuthAtatus(CopyrightAuthStatus.AUTH_FAIL)
        }
        store.userinfo?.authInfo?.setAuthInfo({
          idCard: formData.idCard,
          needMakeUp: false,
          realName: formData.realName,
        })
      })
      .catch(error => {
        message.destroy()
        message.error(graphQLErrorMessage(error) || '实名认证失败')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [
    authAction,
    completionAction,
    setVisible,
    needCompletionRealName,
    store.userinfo,
  ])

  return (
    <MyModal
      visible={visible}
      title="实名认证"
      onCancel={() => setVisible(false)}
      maskStyle={{
        backgroundColor: 'rgba(0,0,0,0.8)',
      }}
      width={448}
      footer={null}
    >
      <MyForm form={form} layout="vertical">
        <MyForm.Item
          name="realName"
          label="姓名"
          rules={
            isDisabled
              ? []
              : [{ required: true }, { min: 2, message: '请输入正确的姓名' }]
          }
        >
          <Input placeholder="请输入姓名" disabled={isDisabled} />
        </MyForm.Item>
        <MyForm.Item
          name="idCard"
          label="身份证号码"
          rules={
            isDisabled
              ? []
              : [
                  { required: true },
                  {
                    pattern: isIdCard,
                    message: '请输入正确的身份证号码',
                  },
                ]
          }
        >
          <Input placeholder="请输入身份证号码" disabled={isDisabled} />
        </MyForm.Item>
        <MyForm.Item className="my-form-item" label="身份证正反面">
          <Row gutter={10}>
            <Col span={12}>
              <MyForm.Item
                name="idCardFrontUrl"
                rules={[{ required: true, message: '请上传身份证反面' }]}
              >
                <PictureCardUpload
                  height={166}
                  bucket={OssBucket.TRADE_PRIVATE}
                  icon={
                    <>
                      <img
                        width={100}
                        src="/images/settings/idcard-front@2x.jpg"
                        alt=""
                        style={{ marginBottom: 8, borderRadius: 8 }}
                      />
                    </>
                  }
                  label={<>身份证反面</>}
                />
              </MyForm.Item>
            </Col>
            <Col span={12}>
              <MyForm.Item
                name="idCardBackUrl"
                rules={[{ required: true, message: '请上传身份证正面' }]}
              >
                <PictureCardUpload
                  height={166}
                  bucket={OssBucket.TRADE_PRIVATE}
                  icon={
                    <>
                      <img
                        width={100}
                        src="/images/settings/idcard-back@2x.jpg"
                        alt=""
                        style={{ marginBottom: 8, borderRadius: 8 }}
                      />
                    </>
                  }
                  label={<>身份证正面</>}
                />
              </MyForm.Item>
            </Col>
          </Row>
        </MyForm.Item>
        <StyledLabel>
          说明：实名认证需要和手机号实名用户相同，平台无法为未满18周岁的用户提供服务。
        </StyledLabel>
        <MyBtn
          onClick={onConfirm}
          style={{ marginBottom: 16 }}
          type="primary"
          size="large"
          loading={loading}
          block
        >
          确认提交
        </MyBtn>
      </MyForm>
    </MyModal>
  )
})
